import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    LinearProgress,
    MenuItem,
    Paper,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ticketService from '../services/TicketService';
import userService from '../services/UserService';

const STATUS_OPTIONS = [
    { value: 'open', label: 'open' },
    { value: 'inprogress', label: 'inprogress' },
    { value: 'In QA', label: 'In QA' },
    { value: 'close', label: 'close' },
];

const PRIORITY_OPTIONS = [
    { value: 'Critical', label: 'Critical' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' },
];

const TYPE_OPTIONS = [
    { value: 'Bug', label: 'Bug' },
    { value: 'Feature', label: 'Feature' },
    { value: 'Enhancement', label: 'Enhancement' },
];

function normalizeStatus(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === 'open') return 'open';
    if (raw === 'inprogress' || raw === 'in_progress' || raw === 'in-progress') return 'inprogress';
    if (raw === 'in qa' || raw === 'inqa' || raw === 'in_qa' || raw === 'in-qa') return 'In QA';
    if (raw === 'close' || raw === 'closed') return 'close';
    return 'open';
}

function normalizePriority(value) {
    const raw = String(value || '').trim();
    const lowered = raw.toLowerCase();

    // Accept common misspelling seen in requirements: "Crititcal"
    if (lowered === 'critical' || lowered === 'crititcal') return 'Critical';
    if (lowered === 'high') return 'High';
    if (lowered === 'medium') return 'Medium';
    if (lowered === 'low') return 'Low';

    return 'Medium';
}

function normalizeTicketType(value) {
    const raw = String(value || '').trim().toLowerCase();

    if (raw === 'feature') return 'Feature';
    if (raw === 'enhancement') return 'Enhancement';
    if (raw === 'bug') return 'Bug';

    return 'Bug';
}

function formatEpoch(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '-';
    const ms = n < 1e12 ? n * 1000 : n;
    try {
        return new Date(ms).toLocaleString();
    } catch (e) {
        return String(value);
    }
}

function parseEstimatedHours(value) {
    const trimmed = String(value || '').trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function formatEstimatedHours(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return '-';
    return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} h`;
}

const defaultGetTicket = (ticketId) => ticketService.getTicket(ticketId);
const defaultUpdateTicket = (ticketId, payload) => ticketService.updateTicket(ticketId, payload);
const defaultUploadAttachment = (ticketId, file, onProgress) => ticketService.uploadTicketAttachment(ticketId, file, onProgress);
const defaultDeleteAttachment = (ticketId, fileId) => ticketService.deleteTicketAttachment(ticketId, fileId);
const defaultAddNote = (ticketId, body) => ticketService.addTicketNote(ticketId, body);

function statusLabel(t, value) {
    switch (normalizeStatus(value)) {
        case 'open':
            return t('tickets.statusOpen', 'Open');
        case 'inprogress':
            return t('tickets.statusInProgress', 'In Progress');
        case 'In QA':
            return t('tickets.statusInQa', 'In QA');
        case 'close':
            return t('tickets.statusClosed', 'Closed');
        default:
            return normalizeStatus(value);
    }
}

function priorityLabel(t, value) {
    switch (normalizePriority(value)) {
        case 'Critical':
            return t('tickets.priorityCritical', 'Critical');
        case 'High':
            return t('tickets.priorityHigh', 'High');
        case 'Medium':
            return t('tickets.priorityMedium', 'Medium');
        case 'Low':
            return t('tickets.priorityLow', 'Low');
        default:
            return normalizePriority(value);
    }
}

function typeLabel(t, value) {
    switch (normalizeTicketType(value)) {
        case 'Bug':
            return t('tickets.typeBug', 'Bug');
        case 'Feature':
            return t('tickets.typeFeature', 'Feature');
        case 'Enhancement':
            return t('tickets.typeEnhancement', 'Enhancement');
        default:
            return normalizeTicketType(value);
    }
}

function formatBytes(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function getAttachmentPreviewType(attachment) {
    const contentType = String(attachment?.contentType || '').toLowerCase();
    const filename = String(attachment?.filename || '').toLowerCase();
    if (contentType.startsWith('image/') || filename.match(/\.(png|jpe?g|gif|webp)$/)) return 'image';
    if (contentType.startsWith('video/') || filename.match(/\.(mp4|mov|m4v|webm|ogv)$/)) return 'video';
    if (contentType === 'application/pdf' || filename.endsWith('.pdf')) return 'pdf';
    return '';
}

function TicketDetails({
    pageTitle,
    backPath = '/tickets',
    backLabel,
    getTicket: getTicketRequest = defaultGetTicket,
    updateTicket: updateTicketRequest = defaultUpdateTicket,
    uploadAttachment: uploadAttachmentRequest = defaultUploadAttachment,
    deleteAttachment: deleteAttachmentRequest = defaultDeleteAttachment,
    addNote: addNoteRequest = defaultAddNote,
    assignmentEnabled = false,
    assigneeProfile = 'admin',
}) {
    const { t } = useTranslation();
    const { ticketId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState('open');
    const [priority, setPriority] = useState('Medium');
    const [ticketType, setTicketType] = useState('Bug');
    const [estimatedHours, setEstimatedHours] = useState('');

    const [createdAt, setCreatedAt] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [createdBy, setCreatedBy] = useState('');
    const [assignedTo, setAssignedTo] = useState('');
    const [adminUsers, setAdminUsers] = useState([]);
    const [loadingAdminUsers, setLoadingAdminUsers] = useState(false);
    const [notes, setNotes] = useState([]);
    const [noteBody, setNoteBody] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const [attachmentUpload, setAttachmentUpload] = useState({ filename: '', progress: 0 });
    const [attachmentToDelete, setAttachmentToDelete] = useState(null);
    const [deletingAttachmentId, setDeletingAttachmentId] = useState('');
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [isDraggingAttachment, setIsDraggingAttachment] = useState(false);
    const attachmentDragDepthRef = useRef(0);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const canSave = useMemo(() => {
        return Boolean(ticketId) && !loading && !saving && title.trim().length > 0;
    }, [ticketId, loading, saving, title]);

    useEffect(() => {
        const load = async () => {
            if (!ticketId) return;
            setLoading(true);
            try {
                const t = await getTicketRequest(ticketId);
                setTitle(t?.title || '');
                setDescription(t?.description || '');
                setStatus(normalizeStatus(t?.status));
                setPriority(normalizePriority(t?.priority));
                setTicketType(normalizeTicketType(t?.type));
                setEstimatedHours(t?.estimatedHours == null ? '' : String(t.estimatedHours));
                setCreatedAt(t?.createdAt ?? null);
                setUpdatedAt(t?.updatedAt ?? null);
                setCreatedBy(t?.createdBy || '');
                setAssignedTo(t?.assignedTo || '');
                setNotes(Array.isArray(t?.notes) ? t.notes : []);
                setAttachments(Array.isArray(t?.attachments) ? t.attachments : []);
            } catch (e) {
                console.error('Failed to load ticket', e);
                setSnackbar({ open: true, message: t('tickets.failedToLoadTicket', 'Failed to load ticket'), severity: 'error' });
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [getTicketRequest, ticketId, t]);

    useEffect(() => {
        if (!assignmentEnabled) return undefined;

        let cancelled = false;
        const loadAdminUsers = async () => {
            setLoadingAdminUsers(true);
            try {
                const data = await userService.getUsers(assigneeProfile);
                if (!cancelled) setAdminUsers(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error('Failed to load admin users', e);
                if (!cancelled) {
                    setAdminUsers([]);
                    setSnackbar({ open: true, message: t('tickets.failedToLoadAdminUsers', 'Failed to load admin users'), severity: 'error' });
                }
            } finally {
                if (!cancelled) setLoadingAdminUsers(false);
            }
        };

        loadAdminUsers();
        return () => { cancelled = true; };
    }, [assignmentEnabled, assigneeProfile, t]);

    const assigneeOptions = useMemo(() => {
        const seen = new Set();
        const options = [];
        adminUsers.forEach((user) => {
            const value = String(user?.name || user?.email || '').trim();
            if (!value || seen.has(value)) return;
            seen.add(value);
            options.push({
                value,
                label: user?.email ? `${value} (${user.email})` : value,
            });
        });
        if (assignedTo && !seen.has(assignedTo)) {
            options.unshift({ value: assignedTo, label: assignedTo });
        }
        return options;
    }, [adminUsers, assignedTo]);

    const handleSave = async () => {
        if (!ticketId) return;

        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
            setSnackbar({ open: true, message: t('tickets.titleRequired', 'Title is required'), severity: 'error' });
            return;
        }
        if (estimatedHours.trim() && parseEstimatedHours(estimatedHours) == null) {
            setSnackbar({ open: true, message: t('tickets.estimatedHoursInvalid', 'Estimated hours must be 0 or greater'), severity: 'error' });
            return;
        }

        setSaving(true);
        try {
            const updated = await updateTicketRequest(ticketId, {
                title: trimmedTitle,
                description: description.trim(),
                status: normalizeStatus(status),
                priority: normalizePriority(priority),
                type: normalizeTicketType(ticketType),
                estimatedHours: parseEstimatedHours(estimatedHours),
                assignedTo: assignedTo.trim(),
            });

            setTitle(updated?.title || trimmedTitle);
            setDescription(updated?.description || description.trim());
            setStatus(normalizeStatus(updated?.status || status));
            setPriority(normalizePriority(updated?.priority || priority));
            setTicketType(normalizeTicketType(updated?.type || ticketType));
            setEstimatedHours(updated?.estimatedHours == null ? estimatedHours.trim() : String(updated.estimatedHours));
            setAssignedTo(updated?.assignedTo || assignedTo.trim());
            setCreatedAt(updated?.createdAt ?? createdAt);
            setUpdatedAt(updated?.updatedAt ?? Date.now());

            setSnackbar({ open: true, message: t('tickets.ticketUpdated', 'Ticket updated'), severity: 'success' });
        } catch (e) {
            console.error('Failed to update ticket', e);
            setSnackbar({ open: true, message: t('tickets.failedToUpdate', 'Failed to update ticket'), severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const applyTicketDetails = (t) => {
        if (!t) return;
        setTitle(t?.title || title);
        setDescription(t?.description || description);
        setStatus(normalizeStatus(t?.status || status));
        setPriority(normalizePriority(t?.priority || priority));
        setTicketType(normalizeTicketType(t?.type || ticketType));
        setEstimatedHours(t?.estimatedHours == null ? estimatedHours : String(t.estimatedHours));
        setCreatedAt(t?.createdAt ?? createdAt);
        setUpdatedAt(t?.updatedAt ?? updatedAt);
        setCreatedBy(t?.createdBy || createdBy);
        setAssignedTo(t?.assignedTo || assignedTo);
        setNotes(Array.isArray(t?.notes) ? t.notes : []);
        setAttachments(Array.isArray(t?.attachments) ? t.attachments : []);
    };

    const handleAddNote = async () => {
        const trimmed = noteBody.trim();
        if (!ticketId || !trimmed) {
            setSnackbar({ open: true, message: t('tickets.noteRequired', 'Note is required'), severity: 'error' });
            return;
        }

        setAddingNote(true);
        try {
            const updated = await addNoteRequest(ticketId, trimmed);
            applyTicketDetails(updated);
            setNoteBody('');
            setSnackbar({ open: true, message: t('tickets.noteAdded', 'Note added'), severity: 'success' });
        } catch (e) {
            console.error('Failed to add note', e);
            setSnackbar({ open: true, message: t('tickets.failedToAddNote', 'Failed to add note'), severity: 'error' });
        } finally {
            setAddingNote(false);
        }
    };

    const uploadAttachmentFile = async (file) => {
        if (!ticketId || !file) return;

        setUploadingAttachment(true);
        setAttachmentUpload({ filename: file.name || 'attachment', progress: 0 });
        try {
            const updated = await uploadAttachmentRequest(ticketId, file, (progress) => {
                setAttachmentUpload({ filename: file.name || 'attachment', progress });
            });
            setAttachmentUpload({ filename: file.name || 'attachment', progress: 100 });
            applyTicketDetails(updated);
            setSnackbar({ open: true, message: t('tickets.attachmentUploaded', 'Attachment uploaded'), severity: 'success' });
        } catch (e) {
            console.error('Failed to upload attachment', e);
            setSnackbar({ open: true, message: t('tickets.failedToUploadAttachment', 'Failed to upload attachment'), severity: 'error' });
        } finally {
            setUploadingAttachment(false);
            setAttachmentUpload({ filename: '', progress: 0 });
        }
    };

    const handleAttachmentChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        await uploadAttachmentFile(file);
    };

    const isFileDrag = (event) => {
        return Array.from(event.dataTransfer?.types || []).includes('Files');
    };

    const handleAttachmentDragEnter = (event) => {
        if (!isFileDrag(event)) return;
        event.preventDefault();
        attachmentDragDepthRef.current += 1;
        setIsDraggingAttachment(true);
    };

    const handleAttachmentDragOver = (event) => {
        if (!isFileDrag(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = ticketId && !loading && !uploadingAttachment ? 'copy' : 'none';
    };

    const handleAttachmentDragLeave = (event) => {
        if (!isFileDrag(event)) return;
        event.preventDefault();
        attachmentDragDepthRef.current = Math.max(attachmentDragDepthRef.current - 1, 0);
        if (attachmentDragDepthRef.current === 0) setIsDraggingAttachment(false);
    };

    const handleAttachmentDrop = async (event) => {
        if (!isFileDrag(event)) return;
        event.preventDefault();
        attachmentDragDepthRef.current = 0;
        setIsDraggingAttachment(false);

        if (!ticketId || loading || uploadingAttachment) return;
        const file = event.dataTransfer?.files?.[0];
        await uploadAttachmentFile(file);
    };

    const handleConfirmDeleteAttachment = async () => {
        const fileId = attachmentToDelete?.fileId;
        if (!ticketId || !fileId) return;

        setDeletingAttachmentId(fileId);
        try {
            const updated = await deleteAttachmentRequest(ticketId, fileId);
            applyTicketDetails(updated);
            if (previewAttachment?.fileId === fileId) setPreviewAttachment(null);
            setAttachmentToDelete(null);
            setSnackbar({ open: true, message: t('tickets.attachmentDeleted', 'Attachment deleted'), severity: 'success' });
        } catch (e) {
            console.error('Failed to delete attachment', e);
            setSnackbar({ open: true, message: t('tickets.failedToDeleteAttachment', 'Failed to delete attachment'), severity: 'error' });
        } finally {
            setDeletingAttachmentId('');
        }
    };

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));
    const previewType = getAttachmentPreviewType(previewAttachment);
    const previewUrl = previewAttachment?.previewUrl || previewAttachment?.downloadUrl || '';

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5" fontWeight="bold">{pageTitle || t('tickets.ticket', 'Ticket')}</Typography>
                <Button variant="outlined" onClick={() => navigate(backPath)} disabled={saving}>
                    {backLabel || t('tickets.backToTickets', 'Back to Tickets')}
                </Button>
            </Box>

            <Paper sx={{ p: 2 }} elevation={1}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(180px, 1fr))',
                            lg: 'repeat(5, minmax(150px, 1fr))',
                        },
                        gap: 1,
                        mb: 2,
                    }}
                >
                    {[
                        [t('tickets.ticketId', 'Ticket ID'), ticketId || '-'],
                        [t('tickets.createdBy', 'Created By'), createdBy || '-'],
                        ...(assignmentEnabled ? [] : [[t('tickets.assignedTo', 'Assigned To'), assignedTo || '-']]),
                        [t('tickets.estimatedHours', 'Estimated Hours'), formatEstimatedHours(estimatedHours)],
                        [t('tickets.created', 'Created'), formatEpoch(createdAt)],
                        [t('tickets.updated', 'Updated'), formatEpoch(updatedAt)],
                    ].map(([label, value]) => (
                        <Box key={label} sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                {label}
                            </Typography>
                            <Typography variant="body2" color="text.primary" noWrap title={String(value)}>
                                {value}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {loading ? <Alert severity="info" sx={{ mb: 2 }}>{t('tickets.loading', 'Loading…')}</Alert> : null}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: assignmentEnabled
                                ? 'minmax(240px, 2fr) repeat(4, minmax(140px, 1fr)) minmax(220px, 1.5fr)'
                                : 'minmax(240px, 2fr) repeat(4, minmax(140px, 1fr))',
                        },
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <TextField
                        label={t('tickets.title', 'Title')}
                        size="small"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={loading || saving}
                    />

                    <TextField
                        label={t('tickets.status', 'Status')}
                        select
                        size="small"
                        value={normalizeStatus(status)}
                        onChange={(e) => setStatus(e.target.value)}
                        disabled={loading || saving}
                    >
                        {STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {statusLabel(t, opt.value)}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label={t('tickets.priority', 'Priority')}
                        select
                        size="small"
                        value={normalizePriority(priority)}
                        onChange={(e) => setPriority(e.target.value)}
                        disabled={loading || saving}
                    >
                        {PRIORITY_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {priorityLabel(t, opt.value)}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label={t('tickets.type', 'Type')}
                        select
                        size="small"
                        value={normalizeTicketType(ticketType)}
                        onChange={(e) => setTicketType(e.target.value)}
                        disabled={loading || saving}
                    >
                        {TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {typeLabel(t, opt.value)}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label={t('tickets.estimatedHours', 'Estimated Hours')}
                        size="small"
                        type="number"
                        value={estimatedHours}
                        onChange={(e) => setEstimatedHours(e.target.value)}
                        disabled={loading || saving}
                        inputProps={{ min: 0, step: 0.25 }}
                    />

                    {assignmentEnabled ? (
                        <TextField
                            label={t('tickets.assignedTo', 'Assigned To')}
                            select
                            size="small"
                            value={assignedTo}
                            onChange={(e) => setAssignedTo(e.target.value)}
                            disabled={loading || saving || loadingAdminUsers}
                            helperText={loadingAdminUsers ? t('tickets.loadingAdminUsers', 'Loading admin users…') : ''}
                        >
                            <MenuItem value="">{t('tickets.unassigned', 'Unassigned')}</MenuItem>
                            {assigneeOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : null}
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                        gap: 1.5,
                        alignItems: 'end',
                    }}
                >
                    <TextField
                        label={t('tickets.description', 'Description')}
                        fullWidth
                        size="small"
                        multiline
                        minRows={3}
                        maxRows={8}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={loading || saving}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!canSave}
                        sx={{ minWidth: 100, height: 40 }}
                    >
                        {t('tickets.save', 'Save')}
                    </Button>
                </Box>
            </Paper>

            <Paper sx={{ p: 2 }} elevation={1}>
                <Typography variant="h6" sx={{ mb: 1 }}>{t('tickets.notes', 'Notes')}</Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                        gap: 1.5,
                        alignItems: 'end',
                        mb: notes.length ? 2 : 0,
                    }}
                >
                    <TextField
                        label={t('tickets.addNote', 'Add Note')}
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        maxRows={5}
                        value={noteBody}
                        onChange={(e) => setNoteBody(e.target.value)}
                        disabled={loading || addingNote}
                    />
                    <Button
                        variant="outlined"
                        onClick={handleAddNote}
                        disabled={!ticketId || addingNote || !noteBody.trim()}
                        sx={{ minWidth: 116, height: 40 }}
                    >
                        {addingNote ? t('tickets.adding', 'Adding…') : t('tickets.addNote', 'Add Note')}
                    </Button>
                </Box>

                {notes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('tickets.noNotes', 'No notes.')}</Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {[...notes].sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0)).map((note, index) => (
                            <Box
                                key={note?.noteId || `${note?.createdAt || ''}-${index}`}
                                sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1.5 }}
                            >
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {note?.body || ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {[note?.createdBy || '-', formatEpoch(note?.createdAt)].filter(Boolean).join(' - ')}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Paper>

            <Paper
                sx={{
                    p: 2,
                    position: 'relative',
                    border: '1px solid',
                    borderColor: isDraggingAttachment ? 'primary.main' : 'transparent',
                    bgcolor: isDraggingAttachment ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
                    transition: 'border-color 120ms ease, background-color 120ms ease',
                }}
                elevation={1}
                onDragEnter={handleAttachmentDragEnter}
                onDragOver={handleAttachmentDragOver}
                onDragLeave={handleAttachmentDragLeave}
                onDrop={handleAttachmentDrop}
            >
                {isDraggingAttachment ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 8,
                            border: '2px dashed',
                            borderColor: 'primary.main',
                            borderRadius: 1,
                            bgcolor: 'rgba(255, 255, 255, 0.72)',
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        <Typography variant="subtitle1" color="primary" fontWeight={600}>
                            {t('tickets.dropAttachmentToUpload', 'Drop attachment to upload')}
                        </Typography>
                    </Box>
                ) : null}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                    <Typography variant="h6">{t('tickets.attachments', 'Attachments')}</Typography>
                    <Button
                        component="label"
                        variant="outlined"
                        disabled={!ticketId || loading || uploadingAttachment}
                    >
                        {uploadingAttachment ? t('tickets.uploading', 'Uploading…') : t('tickets.addAttachment', 'Add Attachment')}
                        <input type="file" hidden onChange={handleAttachmentChange} />
                    </Button>
                </Box>
                {uploadingAttachment ? (
                    <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" noWrap title={attachmentUpload.filename}>
                                {attachmentUpload.filename || t('tickets.uploadingAttachment', 'Uploading attachment')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {attachmentUpload.progress}%
                            </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={attachmentUpload.progress} />
                    </Box>
                ) : null}

                {attachments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('tickets.noAttachments', 'No attachments.')}</Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {attachments.map((attachment, index) => {
                            const filename = attachment?.filename || 'attachment';
                            const meta = [
                                formatBytes(attachment?.size),
                                formatEpoch(attachment?.uploadedAt),
                                attachment?.uploadedBy ? t('tickets.attachedBy', 'Attached by {{name}}', { name: attachment.uploadedBy }) : '',
                            ]
                                .filter(Boolean)
                                .join(' - ');
                            const attachmentPreviewType = getAttachmentPreviewType(attachment);
                            const canPreview = Boolean(attachmentPreviewType && (attachment?.previewUrl || attachment?.downloadUrl));
                            const isDeleting = deletingAttachmentId === attachment?.fileId;
                            return (
                                <Box
                                    key={attachment?.fileId || `${filename}-${index}`}
                                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                                        {canPreview ? (
                                            <Link
                                                component="button"
                                                type="button"
                                                underline="hover"
                                                onClick={() => setPreviewAttachment(attachment)}
                                                sx={{ border: 0, p: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                                            >
                                                {filename}
                                            </Link>
                                        ) : attachment?.downloadUrl ? (
                                            <Link href={attachment.downloadUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                                                {filename}
                                            </Link>
                                        ) : (
                                            <Typography variant="body2">{filename}</Typography>
                                        )}
                                        <Tooltip title={t('tickets.deleteAttachmentTooltip', 'Delete attachment')} placement="top" arrow>
                                            <span>
                                                <IconButton
                                                    aria-label={t('tickets.deleteFileAria', 'Delete {{filename}}', { filename })}
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setAttachmentToDelete(attachment)}
                                                    disabled={isDeleting || uploadingAttachment}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                    {meta ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {isDeleting ? t('tickets.deleting', 'Deleting...') : meta}
                                        </Typography>
                                    ) : null}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Paper>

            <Dialog
                open={Boolean(previewAttachment)}
                onClose={() => setPreviewAttachment(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { overflow: 'hidden' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ flex: 1, fontWeight: 600 }} noWrap>
                        {previewAttachment?.filename || t('tickets.attachmentPreview', 'Attachment Preview')}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: '#f5f5f5', minHeight: 360 }}>
                    {previewAttachment && previewType === 'image' ? (
                        <Box sx={{ minHeight: 360, height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                            <img
                                src={previewUrl}
                                alt={previewAttachment?.filename || t('tickets.attachmentPreviewAlt', 'Attachment preview')}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                        </Box>
                    ) : null}
                    {previewAttachment && previewType === 'pdf' ? (
                        <iframe
                            src={previewUrl}
                            title={previewAttachment?.filename || t('tickets.attachmentPreviewAlt', 'Attachment preview')}
                            style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
                        />
                    ) : null}
                    {previewAttachment && previewType === 'video' ? (
                        <Box sx={{ minHeight: 360, height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                            <video
                                src={previewUrl}
                                controls
                                style={{ maxWidth: '100%', maxHeight: '100%', backgroundColor: '#000' }}
                            >
                                <track kind="captions" />
                            </video>
                        </Box>
                    ) : null}
                    {previewAttachment && !previewType ? (
                        <Box sx={{ p: 4 }}>
                            <Alert severity="info">{t('tickets.previewUnavailable', 'Preview is available for PDF, image, and video attachments.')}</Alert>
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    {previewAttachment?.downloadUrl ? (
                        <Button href={previewAttachment.downloadUrl} target="_blank" rel="noopener noreferrer">
                            {t('tickets.download', 'Download')}
                        </Button>
                    ) : null}
                    <Button onClick={() => setPreviewAttachment(null)}>{t('tickets.close', 'Close')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(attachmentToDelete)}
                onClose={() => setAttachmentToDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t('tickets.deleteAttachment', 'Delete Attachment')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {t('tickets.deleteAttachmentConfirm', 'Delete {{filename}}?', { filename: attachmentToDelete?.filename || t('tickets.thisAttachment', 'this attachment') })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAttachmentToDelete(null)} disabled={Boolean(deletingAttachmentId)}>
                        {t('tickets.cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDeleteAttachment}
                        disabled={Boolean(deletingAttachmentId)}
                    >
                        {deletingAttachmentId ? t('tickets.deleting', 'Deleting...') : t('tickets.delete', 'Delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default TicketDetails;
